/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Dropdown, MenuProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from 'src/components/ts-components/Text'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import { useState, useEffect, useMemo, ReactNode } from 'react'
import DragHandle from './DragHandle'
import MoreIcon from './MoreIcon'
import SaveIcon from './SaveIcon'
import CancelIcon from './CancelIcon'
import { typeDisplayLabels } from '../../constants/typeDisplayLabels'
import OptionEditor from './OptionEditor'
import NumberRangeInputs from './NumberRangeInputs'
import QuestionTypeSelector from './QuestionTypeSelector'
import {
  Question,
  QuestionFormDraft,
  isChoiceQuestion,
  isHockeyUSAQuestion,
  isHockeyCanadaQuestion,
  isNumberQuestion,
  isTextQuestion,
} from './types'
import {
  isQuestionFormValid,
  getDisabledButtonStyles,
} from 'src/utils/questionFormValidation'
import {
  questionTypeIconMap,
  DefaultQuestionIcon,
} from 'src/components/ts-components/QuestionTypeIcons'
import { SelectInput } from 'src/components/ts-components/SelectInput'

const usaHockeyValidationOptions = [
  { value: 'none', label: 'No Validation' },
  { value: 'partial', label: 'Partial Validation (Last Name + Birth Date)' },
  {
    value: 'full',
    label: 'Full Validation (First/Last Name + Birth Date)',
  },
]

// Hockey Canada mirrors the USA Hockey validation choices.
const hockeyCanadaValidationOptions = [
  { value: 'none', label: 'No Validation' },
  { value: 'partial', label: 'Partial Validation (Last Name + Birth Date)' },
  {
    value: 'full',
    label: 'Full Validation (First/Last Name + Birth Date)',
  },
]

const dropdownOverlayStyle = { zIndex: 10009 }

const renderDropdownOverlay = (menu: ReactNode) => (
  <div
    css={css`
      background: ${colors.SECONDARY_400};
      border-radius: 8px;
      border: 1px solid ${colors.SECONDARY_200};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 4px;
      min-width: 160px;
    `}
  >
    {menu}
  </div>
)

interface DraggableQuestionProps {
  question: Question
  isReserved?: boolean
  onToggle?: (questionId: string, enabled: boolean) => void
  onDelete?: (questionId: string) => void
  onUpdate?: (questionId: string, updatedQuestion: Partial<Question>) => void
  onEditStateChange?: (isEditing: boolean) => void
  fieldErrors?: any
  customError?: any
  isSectionEnabled?: boolean
}

const DraggableQuestion = ({
  question,
  isReserved = false,
  onToggle,
  onDelete,
  onUpdate,
  onEditStateChange,
  fieldErrors = {},
  customError = {},
  isSectionEnabled = true,
}: DraggableQuestionProps) => {
  const isEnabled = question.enabled !== false // Default to true if not specified
  const [isEditing, setIsEditing] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: question.field_id,
    disabled: isEditing, // Disable dragging when editing
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const [editForm, setEditForm] = useState<QuestionFormDraft>({
    label: question.label,
    displayLabel: question.displayLabel || '',
    type: question.type as Question['type'],
    placeholder: question.placeholder,
    description: question.description || '',
    mandatory: question.mandatory,
    options:
      question.type === 'dropdown' && isChoiceQuestion(question)
        ? question.options
        : [],
    min: isNumberQuestion(question) ? question.min?.toString() || '' : '',
    max: isNumberQuestion(question) ? question.max?.toString() || '' : '',
    max_char: isTextQuestion(question)
      ? question.max_char?.toString() || ''
      : '',
    validate_schedule_dates: isHockeyUSAQuestion(question)
      ? question.validate_schedule_dates
      : false,
    usa_hockey_validation_type: isHockeyUSAQuestion(question)
      ? question.usa_hockey_validation_type
      : 'none',
    hockey_canada_validation_type: isHockeyCanadaQuestion(question)
      ? question.hockey_canada_validation_type
      : 'none',
  })

  // Update editForm when question prop changes (after save)
  useEffect(() => {
    setEditForm({
      label: question.label,
      displayLabel: question.displayLabel || '',
      type: question.type as Question['type'],
      placeholder: question.placeholder,
      description: question.description || '',
      mandatory: question.mandatory,
      options:
        question.type === 'dropdown' && isChoiceQuestion(question)
          ? question.options
          : [],
      min: isNumberQuestion(question) ? question.min?.toString() || '' : '',
      max: isNumberQuestion(question) ? question.max?.toString() || '' : '',
      max_char: isTextQuestion(question)
        ? question.max_char?.toString() || ''
        : '',
      validate_schedule_dates: isHockeyUSAQuestion(question)
        ? question.validate_schedule_dates
        : false,
      usa_hockey_validation_type: isHockeyUSAQuestion(question)
        ? question.usa_hockey_validation_type
        : 'none',
      hockey_canada_validation_type: isHockeyCanadaQuestion(question)
        ? question.hockey_canada_validation_type
        : 'none',
    })
  }, [question])

  // Notify parent when editing state changes
  useEffect(() => {
    onEditStateChange?.(isEditing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  // Check if this is a USA Hockey field (has its own dedicated editor)
  const isHockeyUSA = question.type === 'hockey_usa'

  // Hockey Canada mirrors USA Hockey's dedicated editor
  const isHockeyCanada = question.type === 'hockey_canada'

  // Either membership field: both are locked-title, always-required editors,
  // so all the shared UI guards below treat them identically.
  const isHockeyMembership = isHockeyUSA || isHockeyCanada

  // Reserved is driven entirely by the API. A reserved field is fully locked:
  // no action menu, no edit, no delete — the user cannot interact with it.
  const questionIsReserved = question.isReserved || isReserved

  // Validate the Edit Question form
  const isEditFormValid = useMemo(() => {
    // USA Hockey has its own special handling - always valid since fields are fixed
    const isHockeyUSAEditMode = isHockeyMembership
    return isHockeyUSAEditMode || isQuestionFormValid(editForm)
  }, [editForm, isHockeyMembership])

  const handleEditClick = () => {
    // Reserved fields are fully locked and cannot be edited (USA Hockey, which
    // is not reserved, keeps its own editor).
    if (questionIsReserved) {
      return
    }

    setEditForm({
      label: question.label,
      displayLabel: question.displayLabel || '',
      type: question.type as Question['type'],
      placeholder: question.placeholder,
      description: question.description || '',
      mandatory: question.mandatory,
      options:
        question.type === 'dropdown' && isChoiceQuestion(question)
          ? question.options
          : [],
      min: isNumberQuestion(question) ? question.min?.toString() || '' : '',
      max: isNumberQuestion(question) ? question.max?.toString() || '' : '',
      max_char: isTextQuestion(question)
        ? question.max_char?.toString() || ''
        : '',
      validate_schedule_dates: isHockeyUSAQuestion(question)
        ? question.validate_schedule_dates
        : false,
      usa_hockey_validation_type: isHockeyUSAQuestion(question)
        ? question.usa_hockey_validation_type
        : 'none',
      hockey_canada_validation_type: isHockeyCanadaQuestion(question)
        ? question.hockey_canada_validation_type
        : 'none',
    })
    setIsEditing(true)
  }

  const handleSave = () => {
    if (isHockeyUSA) {
      // USA Hockey mode - only update the mandatory field, keep fixed title
      if (onUpdate) {
        const updatedQuestion = {
          field_id: question.field_id,
          mandatory: true, // USA Hockey is always required
          label: 'USA Hockey Number', // Fixed title
          type: question.type,
          validate_schedule_dates: editForm.validate_schedule_dates,
          usa_hockey_validation_type: editForm.usa_hockey_validation_type,
        } as Partial<Question>
        onUpdate(question.field_id, updatedQuestion)
        setIsEditing(false)
      }
    } else if (isHockeyCanada) {
      // Hockey Canada mode - only update the mandatory field, keep fixed title
      if (onUpdate) {
        const updatedQuestion = {
          field_id: question.field_id,
          mandatory: true, // Hockey Canada is always required
          label: 'Hockey Canada Player Number', // Fixed title
          type: question.type,
          validate_schedule_dates: editForm.validate_schedule_dates,
          hockey_canada_validation_type: editForm.hockey_canada_validation_type,
        } as Partial<Question>
        onUpdate(question.field_id, updatedQuestion)
        setIsEditing(false)
      }
    } else if (editForm.label.trim() && onUpdate) {
      // Full edit mode - update all fields
      let updatedQuestion: Partial<Question> = {
        field_id: question.field_id,
        label: editForm.label,
        type: editForm.type,
        subtype: editForm.type,
        placeholder: editForm.placeholder,
        description: editForm.description,
        mandatory: editForm.mandatory,
      }

      if (editForm.type === 'dropdown') {
        // Validate that at least one option has a label
        const validOptions = editForm.options.filter((opt) => opt.label.trim())
        if (validOptions.length === 0) {
          return // Don't save if no valid options
        }
        updatedQuestion = {
          ...updatedQuestion,
          options: validOptions,
        } as Partial<Question>
      } else if (editForm.type === 'number') {
        updatedQuestion = {
          ...updatedQuestion,
          min: editForm.min ? Number(editForm.min) : undefined,
          max: editForm.max ? Number(editForm.max) : undefined,
        } as Partial<Question>
      } else if (['text', 'textarea', 'email', 'url'].includes(editForm.type)) {
        updatedQuestion = {
          ...updatedQuestion,
          max_char: editForm.max_char ? Number(editForm.max_char) : undefined,
        } as Partial<Question>
      }

      onUpdate(question.field_id, updatedQuestion)
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setEditForm({
      label: question.label,
      displayLabel: question.displayLabel || '',
      type: question.type as Question['type'],
      placeholder: question.placeholder,
      description: question.description || '',
      mandatory: question.mandatory,
      options:
        question.type === 'dropdown' && isChoiceQuestion(question)
          ? question.options
          : [],
      min: isNumberQuestion(question) ? question.min?.toString() || '' : '',
      max: isNumberQuestion(question) ? question.max?.toString() || '' : '',
      max_char: isTextQuestion(question)
        ? question.max_char?.toString() || ''
        : '',
      validate_schedule_dates: isHockeyUSAQuestion(question)
        ? question.validate_schedule_dates || false
        : false,
      usa_hockey_validation_type: isHockeyUSAQuestion(question)
        ? question.usa_hockey_validation_type
        : 'none',
      hockey_canada_validation_type: isHockeyCanadaQuestion(question)
        ? question.hockey_canada_validation_type
        : 'none',
    })
    setIsEditing(false)
  }

  // Reserved fields are fully locked: no action menu at all (USA Hockey, which
  // is not reserved, keeps its menu).
  let shouldDisplayMoreMenu = true
  if (questionIsReserved && !isHockeyMembership) {
    shouldDisplayMoreMenu = false
  }
  if (isEditing) {
    shouldDisplayMoreMenu = false
  }

  const menuItems: MenuProps['items'] = [
    // Show "Edit Question" if not reserved (USA Hockey keeps its editor).
    ...(questionIsReserved && !isHockeyMembership
      ? []
      : [
          {
            key: 'edit',
            label: (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 4px 0;
                `}
              >
                <EditOutlined
                  css={css`
                    color: ${colors.PRIMARY};
                    font-size: 14px;
                  `}
                />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  {isHockeyUSA
                    ? 'Edit USA Hockey Number'
                    : isHockeyCanada
                    ? 'Edit Hockey Canada Player Number'
                    : 'Edit Question'}
                </Text>
              </div>
            ),
            onClick: handleEditClick,
          },
        ]),
    // Enable/Disable toggle - only for non-reserved questions. Clicking the row
    // toggles it directly (no inline switch).
    ...(questionIsReserved
      ? []
      : [
          {
            key: 'toggle-enabled',
            label: (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 4px 0;
                `}
              >
                {isEnabled ? (
                  <StopOutlined
                    css={css`
                      color: ${colors.PRIMARY};
                      font-size: 14px;
                    `}
                  />
                ) : (
                  <CheckCircleOutlined
                    css={css`
                      color: ${colors.PRIMARY};
                      font-size: 14px;
                    `}
                  />
                )}
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  {isEnabled ? 'Disable Question' : 'Enable Question'}
                </Text>
              </div>
            ),
            onClick: ({ domEvent }) => {
              // Prevent the card's onClick (which opens edit) from firing.
              domEvent.stopPropagation()
              onToggle?.(question.field_id, !isEnabled)
            },
          },
        ]),
    // Only show "Delete Question" if not reserved
    ...(questionIsReserved
      ? []
      : [
          {
            key: 'delete',
            label: (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 4px 0;
                `}
              >
                <DeleteOutlined
                  css={css`
                    color: ${colors.ERROR_300};
                    font-size: 14px;
                  `}
                />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Delete Question
                </Text>
              </div>
            ),
            onClick: () => onDelete?.(question.field_id),
          },
        ]),
  ]

  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          background: ${colors.SECONDARY_300};
          border-radius: 8px;
          border: 1px solid ${colors.PRIMARY};
        `}
      >
        <div
          css={css`
            flex: 1 1 0;
            align-self: stretch;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              flex: 1 1 0;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 8px;
              display: inline-flex;
            `}
          >
            {!isHockeyMembership ? (
              <>
                {/* Label and Type Row */}
                <div
                  css={css`
                    align-self: stretch;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 16px;
                    display: inline-flex;
                  `}
                >
                  <div
                    css={css`
                      flex: 1 1 0;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <QuestionTypeSelector
                      label='Question Type *'
                      disabled={false}
                      name={`edit-question-type-${question.field_id}`}
                      value={editForm.type}
                      onChange={(value) =>
                        setEditForm({
                          ...editForm,
                          type: value as Question['type'],
                        })
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      placeholder='Select Question Type'
                    />
                  </div>
                  <div
                    css={css`
                      flex: 1 1 0;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <TextInput
                      name={`edit-question-label-${question.field_id}`}
                      value={editForm.label}
                      setValue={(value) =>
                        setEditForm({ ...editForm, label: value })
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Question Title *'
                      placeholder='Enter Question Title'
                    />
                  </div>
                </div>
              </>
            ) : (
              /* Hockey membership mode - show fixed info as read-only */
              <div
                css={css`
                  align-self: stretch;
                  padding: 8px;
                  background: ${colors.SECONDARY_200};
                  border-radius: 4px;
                  margin-bottom: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.WHITE}
                >
                  {isHockeyCanada
                    ? 'Hockey Canada Player Number'
                    : 'USA Hockey Number'}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {isHockeyCanada
                    ? 'Hockey Canada Player Number Field (Always Required)'
                    : 'USA Hockey Number Field (Always Required)'}
                </Text>
              </div>
            )}

            {/* Conditional fields based on question type - only show in full edit mode and not USA Hockey */}
            {!isHockeyMembership && editForm.type === 'dropdown' && (
              <div
                css={css`
                  align-self: stretch;
                  width: 100%;
                `}
              >
                <OptionEditor
                  options={editForm.options}
                  onChange={(options) => setEditForm({ ...editForm, options })}
                />
              </div>
            )}

            {!isHockeyMembership && editForm.type === 'number' && (
              <div
                css={css`
                  align-self: stretch;
                  width: 100%;
                `}
              >
                <NumberRangeInputs
                  min={editForm.min}
                  max={editForm.max}
                  onMinChange={(min) => setEditForm({ ...editForm, min })}
                  onMaxChange={(max) => setEditForm({ ...editForm, max })}
                  fieldErrors={fieldErrors}
                  customError={customError}
                />
              </div>
            )}

            {/* Description and Max Character fields side by side for text and textarea */}
            {!isHockeyMembership &&
              ['text', 'textarea', 'checkbox'].includes(editForm.type) && (
                <div
                  css={css`
                    align-self: stretch;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 16px;
                    display: inline-flex;
                    width: 100%;
                  `}
                >
                  <div
                    css={css`
                      flex: 1 1 0;
                      flex-direction: column;
                      justify-content: flex-start;
                      align-items: flex-start;
                      gap: 8px;
                      display: inline-flex;
                    `}
                  >
                    <TextInput
                      name={`edit-question-description-${question.field_id}`}
                      value={editForm.description}
                      setValue={(value) =>
                        setEditForm({ ...editForm, description: value })
                      }
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Description (optional)'
                      placeholder='Enter question description'
                    />
                  </div>
                  {/* Only show max character field for text and textarea, not checkbox */}
                  {['text', 'textarea'].includes(editForm.type) && (
                    <div
                      css={css`
                        flex: 1 1 0;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: flex-start;
                        gap: 8px;
                        display: inline-flex;
                      `}
                    >
                      <TextInput
                        name={`edit-question-max-char-${question.field_id}`}
                        value={editForm.max_char}
                        setValue={(value) =>
                          setEditForm({ ...editForm, max_char: value })
                        }
                        fieldErrors={fieldErrors}
                        customError={customError}
                        label='Maximum Characters (optional)'
                        placeholder='Enter Maximum Character Limit'
                        type='number'
                      />
                    </div>
                  )}
                </div>
              )}

            {/* Max Character Limit for other text-like fields (email, url) */}
            {!isHockeyMembership &&
              ['email', 'url'].includes(editForm.type) && (
                <div
                  css={css`
                    flex: 1 1 0;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 8px;
                    display: inline-flex;
                    width: 100%;
                  `}
                >
                  <TextInput
                    name={`edit-question-max-char-${question.field_id}`}
                    value={editForm.max_char}
                    setValue={(value) =>
                      setEditForm({ ...editForm, max_char: value })
                    }
                    fieldErrors={fieldErrors}
                    customError={customError}
                    label='Maximum Characters (optional)'
                    placeholder='Enter Maximum Character Limit'
                    type='number'
                  />
                </div>
              )}

            {/* Description only for country and player_position fields */}
            {!isHockeyMembership &&
              (editForm.type === 'country' ||
                editForm.type === 'player_position') && (
                <div
                  css={css`
                    flex: 1 1 0;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 8px;
                    display: inline-flex;
                    width: 100%;
                  `}
                >
                  <TextInput
                    name={`edit-question-description-${question.field_id}`}
                    value={editForm.description}
                    setValue={(value) =>
                      setEditForm({ ...editForm, description: value })
                    }
                    fieldErrors={fieldErrors}
                    customError={customError}
                    label='Description (Optional)'
                    placeholder='Enter Question Description'
                  />
                </div>
              )}

            {/* Validation Type dropdown for hockey_usa */}
            {isHockeyUSA && editForm.type === 'hockey_usa' && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  width: 50%;
                  margin-top: 8px;
                `}
              >
                <SelectInput
                  label='Validation Type'
                  disabled={false}
                  options={usaHockeyValidationOptions}
                  name='usa_hockey_validation_type'
                  value={editForm.usa_hockey_validation_type}
                  onChange={(value) =>
                    setEditForm({
                      ...editForm,
                      usa_hockey_validation_type: value as
                        | 'none'
                        | 'full'
                        | 'partial',
                    })
                  }
                  fieldErrors={{}}
                  customError={{}}
                  showSearch={false}
                  allowClear={false}
                />
              </div>
            )}

            {/* Validation Type dropdown for hockey_canada */}
            {isHockeyCanada && editForm.type === 'hockey_canada' && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  width: 50%;
                  margin-top: 8px;
                `}
              >
                <SelectInput
                  label='Validation Type'
                  disabled={false}
                  options={hockeyCanadaValidationOptions}
                  name='hockey_canada_validation_type'
                  value={editForm.hockey_canada_validation_type}
                  onChange={(value) =>
                    setEditForm({
                      ...editForm,
                      hockey_canada_validation_type: value as
                        | 'none'
                        | 'full'
                        | 'partial',
                    })
                  }
                  fieldErrors={{}}
                  customError={{}}
                  showSearch={false}
                  allowClear={false}
                />
              </div>
            )}

            {/* Action Buttons and Required Switch */}
            <div
              css={css`
                align-self: stretch;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
                display: inline-flex;
                margin-top: 8px;
              `}
            >
              {/* Switches for hockey_usa */}
              {((isHockeyUSA && editForm.type === 'hockey_usa') ||
                (isHockeyCanada && editForm.type === 'hockey_canada')) && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.WHITE}
                  >
                    Validate Date Segment (Ex. 2023-2024 Season)
                  </Text>
                  <SwitchInput
                    value={editForm.validate_schedule_dates}
                    onChange={(checked) => {
                      setEditForm((prev) => ({
                        ...prev,
                        validate_schedule_dates: checked,
                      }))
                    }}
                    title=''
                    subtitle=''
                  />
                </div>
              )}
              {/* Required Switch - Left Side */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                >
                  Required
                </Text>
                <SwitchInput
                  value={isHockeyMembership ? true : editForm.mandatory}
                  onChange={
                    isHockeyMembership
                      ? () => {} // No-op function for USA Hockey
                      : (value) =>
                          setEditForm({ ...editForm, mandatory: value })
                  }
                  disabled={isHockeyMembership}
                  title=''
                  subtitle=''
                />
              </div>

              {/* Action Buttons - Right Side */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <button
                  css={css`
                    border: 1px solid ${colors.NEUTRAL_0};
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: transparent;
                    cursor: pointer;
                    &:hover {
                      opacity: 0.9;
                    }
                  `}
                  onClick={handleCancel}
                >
                  <CancelIcon size={24} color={colors.NEUTRAL_0} />
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.NEUTRAL_0}
                  >
                    Cancel
                  </Text>
                </button>
                <button
                  disabled={!isEditFormValid}
                  css={css`
                    display: flex;
                    padding: 4px 8px;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    background: ${colors.PRIMARY};
                    border: none;
                    ${getDisabledButtonStyles(!isEditFormValid)}
                    &:hover:not(:disabled) {
                      opacity: 0.9;
                    }
                  `}
                  onClick={handleSave}
                >
                  <SaveIcon size={24} color={PRIMARY_BUTTON_CONTENT.icon} />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={PRIMARY_BUTTON_CONTENT.text}
                  >
                    Save
                  </Text>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Only non-reserved questions (and USA Hockey) can be opened for edit.
  const canEditOnClick = !questionIsReserved || isHockeyMembership

  const handleCardClick = () => {
    if (canEditOnClick) {
      handleEditClick()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;
        padding: 8px;
        background: ${colors.SECONDARY_300};
        border-radius: 8px;
        border: 1px solid ${colors.SECONDARY_300};

        &:hover .question-drag-handle {
          opacity: 1;
        }
      `}
    >
      {/* Question Content - Question Info, Drag Handle, More Menu on Same Row */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 8px;
          position: relative;
        `}
      >
        {/* Question Info */}
        <div
          css={css`
            flex: 1;
            display: flex;
            align-items: flex-start;
            gap: 8px;
            opacity: ${!isSectionEnabled ? 0.8 : isEnabled ? 1 : 0.5};
          `}
        >
          {/* Question Type Icon */}
          <div
            css={css`
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 24px;
              height: 24px;
              align-self: center;
            `}
          >
            {(() => {
              const IconComponent =
                questionTypeIconMap[question.type] || DefaultQuestionIcon
              return <IconComponent size={24} color={colors.NEUTRAL_50} />
            })()}
          </div>

          {/* Question Text Content */}
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              {question.displayLabel || question.label}
            </Text>
            {question.description &&
              [
                'text',
                'textarea',
                'country',
                'player_position',
                'checkbox',
              ].includes(question.type) && (
                <div
                  css={css`
                    margin-top: 2px;
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_30}
                  >
                    {question.description}
                  </Text>
                </div>
              )}
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {
                typeDisplayLabels[
                  question.type as keyof typeof typeDisplayLabels
                ]
              }
              {question.mandatory && ' • Required'}
              {questionIsReserved && ' • Reserved'}
              {isChoiceQuestion(question) && question.options.length > 0 && (
                <>
                  {' • '}
                  {question.options
                    .slice(0, 3)
                    .map((opt) => opt.label)
                    .join(', ')}
                  {question.options.length > 3 &&
                    ` + ${question.options.length - 3} more`}
                </>
              )}
              {isNumberQuestion(question) &&
                (question.min !== undefined || question.max !== undefined) && (
                  <>
                    {' • Range: '}
                    {question.min !== undefined ? question.min : '∞'}
                    {' – '}
                    {question.max !== undefined ? question.max : '∞'}
                  </>
                )}
              {isTextQuestion(question) && question.max_char && (
                <>
                  {' • Max: '}
                  {question.max_char}
                  {' chars'}
                </>
              )}
            </Text>
          </div>
        </div>

        {/* Drag Handle - Centered and Top Aligned, visible on hover */}
        <div
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className='question-drag-handle'
          css={css`
            position: absolute;
            left: 50%;
            top: -6px;
            transform: translateX(-50%);
            display: flex;
            align-items: center;
            justify-content: center;
            align-self: center;
            width: 24px;
            height: 24px;
            cursor: grab;
            border-radius: 4px;
            opacity: ${isDragging ? 1 : 0};
            transition: opacity 0.15s ease-in-out;
            &:hover {
              background: rgba(255, 255, 255, 0.1);
            }
            &:active {
              cursor: grabbing;
            }
          `}
        >
          <DragHandle size={16} color={colors.NEUTRAL_50} />
        </div>

        {/* More Menu */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            align-self: center;
            gap: 8px;
          `}
        >
          {/* Read-only visibility status (toggle lives in the action menu).
              Shown for reserved rows too; hidden when the whole section is
              disabled. */}
          {isSectionEnabled && (
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                gap: 6px;
                color: ${isEnabled ? colors.PRIMARY : colors.NEUTRAL_50};
                font-size: 12px;
                font-weight: 500;
                line-height: 16px;
                white-space: nowrap;
              `}
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
          {shouldDisplayMoreMenu ? (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement='bottomRight'
              overlayStyle={dropdownOverlayStyle}
              overlayClassName='question-dropdown'
              dropdownRender={renderDropdownOverlay}
              disabled={!isSectionEnabled}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 24px;
                  height: 24px;
                  cursor: ${isSectionEnabled ? 'pointer' : 'not-allowed'};
                  opacity: ${isSectionEnabled ? 1 : 0.4};
                  border-radius: 4px;
                  &:hover {
                    background: ${isSectionEnabled
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'transparent'};
                  }
                `}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreIcon size={24} color={colors.NEUTRAL_0} />
              </div>
            </Dropdown>
          ) : (
            /* Placeholder to keep the status column aligned across rows that
               have no action menu (e.g. reserved fields). */
            <div
              css={css`
                width: 24px;
                height: 24px;
              `}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default DraggableQuestion
